import { useId, useState } from 'react';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import {
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApprovalChains } from '@/entities/approval-chain';
import { useSetFolderApprovalChain } from '@/features/manage-project-folders';
import { FormDialog } from '@/shared/ui/FormDialog';

// eslint-disable-next-line i18next/no-literal-string -- select value for "no chain", never rendered
const NO_APPROVAL_CHAIN = 'none';

interface FolderApprovalChainDialogProps {
  projectId: string;
  folder: FolderResponseDto;
  open: boolean;
  onClose: () => void;
}

/** Picks the chain new uploads into a folder get, and optionally its subfolders'. */
export function FolderApprovalChainDialog(props: FolderApprovalChainDialogProps) {
  const { t } = useTranslation('projects');
  const { folder, projectId } = props;
  const selectId = useId();
  const subfoldersId = useId();

  const { data, isLoading } = useApprovalChains(projectId);
  const approvalChains = data?.items ?? [];

  // A deleted chain no longer applies, so the folder reads as having none.
  const recordedChainIsLive = approvalChains.some(
    (approvalChain) => approvalChain.id === folder.autoApplyWorkflowId,
  );
  const recordedValue =
    recordedChainIsLive && folder.autoApplyWorkflowId
      ? folder.autoApplyWorkflowId
      : NO_APPROVAL_CHAIN;
  const [pickedValue, setPickedValue] = useState<string>();
  const value = pickedValue ?? recordedValue;
  const [includeSubfolders, setIncludeSubfolders] = useState(false);

  const setApprovalChain = useSetFolderApprovalChain({
    projectId,
    folderId: folder.id,
    onSuccess: props.onClose,
  });

  const unchanged = value === recordedValue && !includeSubfolders;

  return (
    <FormDialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={t('projectDetail.folderApprovalChainDialog.setApprovalChain')}
      description={t('projectDetail.folderApprovalChainDialog.newUploadsGetThisChain', {
        folderName: folder.name,
      })}
      size="sm"
      data-testid="folder-approval-chain-dialog"
    >
      <FormDialog.Body className="flex flex-col gap-md">
        <div className="flex flex-col gap-xs">
          <Label htmlFor={selectId}>
            {t('projectDetail.folderApprovalChainDialog.approvalChain')}
          </Label>
          <Select
            // Radix shows the placeholder only for an empty value, as the asset's chain select does.
            value={value === NO_APPROVAL_CHAIN ? '' : value}
            onValueChange={setPickedValue}
            disabled={isLoading}
          >
            <SelectTrigger id={selectId} size="lg" data-testid="folder-approval-chain-select">
              <SelectValue
                placeholder={t('projectDetail.folderApprovalChainDialog.noApprovalChain')}
              />
            </SelectTrigger>
            <SelectContent>
              {approvalChains.map((approvalChain) => (
                <SelectItem key={approvalChain.id} value={approvalChain.id}>
                  {approvalChain.name}
                </SelectItem>
              ))}
              {value !== NO_APPROVAL_CHAIN && (
                <>
                  <SelectSeparator />
                  <SelectItem value={NO_APPROVAL_CHAIN}>
                    <XIcon className="size-4" aria-hidden />
                    {t('projectDetail.folderApprovalChainDialog.clear')}
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        {folder.foldersCount > 0 && (
          <div className="flex items-center gap-xs">
            <Checkbox
              id={subfoldersId}
              checked={includeSubfolders}
              onCheckedChange={(next) => setIncludeSubfolders(next === true)}
            />
            <Label htmlFor={subfoldersId} className="font-normal">
              {t('projectDetail.folderApprovalChainDialog.alsoApplyToSubfolders')}
            </Label>
          </div>
        )}
      </FormDialog.Body>
      <FormDialog.Footer
        secondary={{
          label: t('projectDetail.folderApprovalChainDialog.cancel'),
          onClick: props.onClose,
        }}
        primary={{
          label: t('projectDetail.folderApprovalChainDialog.save'),
          onClick: () =>
            setApprovalChain.mutate({
              approvalChainId: value === NO_APPROVAL_CHAIN ? null : value,
              includeSubfolders,
            }),
          disabled: isLoading || unchanged,
          loading: setApprovalChain.isPending,
          'data-testid': 'folder-approval-chain-save',
        }}
      />
    </FormDialog>
  );
}
